import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db } from './config';

// Collection name for partnership requests
const PARTNERSHIP_COLLECTION = 'PartnershipRequests';

/**
 * Submit a partnership request
 * @param {Object} formData - Partnership request data
 * @param {string} formData.companyName - Company name
 * @param {string} formData.email - Contact email
 * @param {string} formData.name - Contact name
 * @param {string} formData.phone - Contact phone (optional)
 * @param {string} formData.message - Additional message (optional)
 * @param {string} formData.useCase - Use case description (optional)
 * @returns {Promise<string>} - Document ID of the created partnership request
 */
export const submitPartnershipRequest = async (formData) => {
  try {
    // Validate required fields
    if (!formData.companyName || !formData.email || !formData.name) {
      throw new Error('Company name, email, and name are required');
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      throw new Error('Invalid email format');
    }

    console.log('🔄 Submitting partnership request:', formData.email);
    
    const docRef = await addDoc(collection(db, PARTNERSHIP_COLLECTION), {
      companyName: formData.companyName.trim(),
      email: formData.email.toLowerCase().trim(),
      name: formData.name.trim(),
      phone: formData.phone?.trim() || '',
      message: formData.message?.trim() || '',
      useCase: formData.useCase?.trim() || '',
      timestamp: serverTimestamp(),
      status: 'pending', // pending, approved, rejected
      accessGranted: false, // Will be set to true when approved
      createdAt: new Date().toISOString(),
      ipAddress: '', // Can be added if needed
      userAgent: navigator.userAgent || ''
    });
    
    console.log('✅ Partnership request submitted with ID: ', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Error submitting partnership request: ', error);
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. Please check your Firebase security rules.');
    } else if (error.code === 'unavailable') {
      throw new Error('Firebase service is currently unavailable. Please try again later.');
    } else if (error.code === 'invalid-argument') {
      throw new Error('Invalid data provided. Please check your input.');
    } else if (error.message === 'Firebase configuration is incomplete. Please check your .env file.') {
      throw new Error('Firebase configuration error. Please contact support.');
    }
    
    throw error;
  }
};

/**
 * Check if an email has submitted a partnership request
 * @param {string} email - Email address to check
 * @returns {Promise<boolean>} - True if request exists
 */
export const hasPartnershipRequest = async (email) => {
  try {
    if (!email) return false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return false;
    }

    console.log('🔄 Checking partnership request for:', email);
    
    const q = query(
      collection(db, PARTNERSHIP_COLLECTION),
      where('email', '==', email.toLowerCase().trim())
    );
    
    const querySnapshot = await getDocs(q);
    const hasRequest = !querySnapshot.empty;
    
    console.log('✅ Partnership request check result:', hasRequest);
    return hasRequest;
  } catch (error) {
    console.error('❌ Error checking partnership request: ', error);
    // Return false on error to allow access (fail open for better UX)
    return false;
  }
};

/**
 * Check if an email has been granted access (approved partnership request)
 * @param {string} email - Email address to check
 * @returns {Promise<boolean>} - True if access is granted
 */
export const hasAccessGranted = async (email) => {
  try {
    if (!email) return false;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return false;
    }

    console.log('🔄 Checking access grant for:', email);
    
    const q = query(
      collection(db, PARTNERSHIP_COLLECTION),
      where('email', '==', email.toLowerCase().trim()),
      where('accessGranted', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const hasAccess = !querySnapshot.empty;
    
    console.log('✅ Access grant check result:', hasAccess);
    return hasAccess;
  } catch (error) {
    console.error('❌ Error checking access grant: ', error);
    // Return false on error (fail closed for security)
    return false;
  }
};

/**
 * Get all partnership requests (for admin purposes)
 * @returns {Promise<Array>} - Array of partnership requests
 */
export const getPartnershipRequests = async () => {
  try {
    const { getDocs } = await import('firebase/firestore');
    console.log('🔄 Fetching partnership requests...');
    
    const querySnapshot = await getDocs(collection(db, PARTNERSHIP_COLLECTION));
    const requests = [];
    
    querySnapshot.forEach((doc) => {
      requests.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    console.log('✅ Retrieved', requests.length, 'partnership requests');
    return requests;
  } catch (error) {
    console.error('❌ Error getting partnership requests: ', error);
    
    // Provide more specific error messages
    if (error.code === 'permission-denied') {
      throw new Error('Permission denied. Please check your Firebase security rules.');
    } else if (error.code === 'unavailable') {
      throw new Error('Firebase service is currently unavailable. Please try again later.');
    }
    
    throw error;
  }
};
